import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ban, CalendarDays, Check, Copy, Lock, Pencil, Plus, RotateCcw, Search, Tag, Trash2, UserRound } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import { localeOf } from '../../assets/js/locale';
import ActionMenu from '../common/ActionMenu';
import Checkbox from '../common/Checkbox';
import FormField from '../common/FormField';
import Loader from '../common/Loader';
import Modal from '../common/Modal';
import Pagination from '../common/Pagination';
import SectionEyebrow from '../common/SectionEyebrow';
import SelectMenu from '../common/SelectMenu';
import '../../assets/styles/DashboardList.css';
import '../../assets/styles/ChooseSurvey.css';
import '../../assets/styles/TemplateBuilder.css';
import '../../assets/styles/ProfileArea.css';
import '../../assets/styles/Discounts.css';

const STATUS_FILTERS = ['all', 'active', 'pending', 'expired', 'exhausted', 'inactive'];
const PAGE_SIZE = 10;

const emptyForm = () => ({
  codigo_promocional: '',
  fecha_inicio: '',
  fecha_fin: '',
  tipo_privilegio: 2,
  hasEndDate: false,
  limite_usos: '',
  hasUsageLimit: false,
  duracion_dias: '30'
});

const MAX_DURATION_DAYS = 3650;

// Fecha para un <input type="date"> sin que la zona horaria la mueva un dia.
const toDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const DiscountCodes = () => {

  const { t, language } = useLanguage();
  const locale = localeOf(language);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const navigate = useNavigate();


  useEffect(() => {
    checkUserPermissions();
  }, []);

  const checkUserPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/usuario`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = await response.json();

      if ( userData.has_permissions === 5) {
        await loadData(userData.has_permissions);
      } else {
        setNotice({ title: t('dc_title'), text: t('dc_no_permission'), after: () => navigate('/dashboard') });
      }
    } catch (error) {
      console.error('Error:', error);
      navigate('/login');
    }
  };


  const loadData = async (userType) => {
    setLoading(true);
    try {
      if (userType === 5) {
        await fetchDiscountCodes();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState(emptyForm);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchDiscountCodes();
  }, []);

  const fetchDiscountCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/discount-codes', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('dc_error_fetch'));
      }

      setDiscountCodes(data);
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingCode(null);
    setFormData(emptyForm());
    setFormError('');
    setShowCreateModal(true);
  };

  const openEdit = (code) => {
    setEditingCode(code);
    setFormData({
      codigo_promocional: code.codigo_promocional || '',
      fecha_inicio: toDateInput(code.fecha_inicio),
      fecha_fin: toDateInput(code.fecha_fin),
      tipo_privilegio: Number(code.tipo_privilegio) || 2,
      hasEndDate: Boolean(code.fecha_fin),
      limite_usos: code.limite_usos === null || code.limite_usos === undefined ? '' : String(code.limite_usos),
      hasUsageLimit: code.limite_usos !== null && code.limite_usos !== undefined,
      duracion_dias: code.duracion_dias ? String(code.duracion_dias) : ''
    });
    setFormError('');
    setShowCreateModal(true);
  };

  const closeForm = () => {
    setShowCreateModal(false);
    setEditingCode(null);
    setFormError('');
  };

  const sendCode = async (url, method, body, fallbackError) => {
    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || fallbackError);
    return data;
  };

  const handleSubmitForm = async () => {
    setFormError('');
    setError('');
    const limit = formData.hasUsageLimit ? parseInt(formData.limite_usos, 10) : null;
    const endDate = formData.hasEndDate ? formData.fecha_fin || null : null;
    const usesSoFar = editingCode ? Number(editingCode.usos_actuales) || 0 : 0;

    const duration = parseInt(formData.duracion_dias, 10);
    if (!Number.isInteger(duration) || duration < 1 || duration > MAX_DURATION_DAYS) {
      setFormError(t('dc_duration_invalid').replace('{max}', MAX_DURATION_DAYS));
      return;
    }

    if (formData.hasUsageLimit && (!Number.isInteger(limit) || limit < Math.max(1, usesSoFar))) {
      setFormError(usesSoFar > 0
        ? t('dc_limit_below_uses').replace('{count}', usesSoFar)
        : t('dc_limit_invalid'));
      return;
    }

    setSaving(true);
    try {
      if (editingCode) {
        const used = Number(editingCode.usos_actuales) > 0;
        // Un codigo ya usado solo cambia fecha de fin y limite; el resto ni se envia.
        const body = used
          ? { fecha_fin: endDate, limite_usos: limit, duracion_dias: duration }
          : {
              codigo_promocional: formData.codigo_promocional,
              fecha_inicio: formData.fecha_inicio,
              fecha_fin: endDate,
              tipo_privilegio: formData.tipo_privilegio,
              limite_usos: limit,
              duracion_dias: duration
            };
        await sendCode(`/api/discount-codes/${editingCode.id}`, 'PUT', body, t('dc_error_update'));
      } else {
        await sendCode('/api/discount-codes', 'POST', {
          codigo_promocional: formData.codigo_promocional,
          fecha_inicio: formData.fecha_inicio,
          fecha_fin: endDate,
          tipo_privilegio: formData.tipo_privilegio,
          limite_usos: limit,
          duracion_dias: duration
        }, t('dc_error_create'));
      }
      closeForm();
      setFormData(emptyForm());
      fetchDiscountCodes();
    } catch (err) {
      console.error('Error al guardar el código:', err);
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (code) => {
    setError('');
    try {
      await sendCode(`/api/discount-codes/${code.id}`, 'PUT', { activo: Number(code.activo) === 0 }, t('dc_error_update'));
      fetchDiscountCodes();
    } catch (err) {
      console.error('Error al cambiar el estado del código:', err);
      setError(err.message);
    }
  };

  const handleDeleteCode = (id) => {
    const code = discountCodes.find(c => c.id === id);
    setCodeToDelete(code);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!codeToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/discount-codes/${codeToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || t('dc_error_delete'));
      }

      fetchDiscountCodes();
      setShowDeleteConfirm(false);
      setCodeToDelete(null);
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
      setShowDeleteConfirm(false);
      setCodeToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setCodeToDelete(null);
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const codeStatus = (code) => {
    if (Number(code.activo) === 0) return 'inactive';
    const now = new Date();
    const start = new Date(code.fecha_inicio);
    const end = code.fecha_fin ? new Date(code.fecha_fin) : null;

    if (code.limite_usos !== null && code.limite_usos !== undefined && code.usos_actuales >= code.limite_usos) return 'exhausted';
    if (now < start) return 'pending';
    if (end && now > end) return 'expired';
    return 'active';
  };

  const statusLabel = {
    active: t('dc_status_active'),
    pending: t('dc_status_pending'),
    expired: t('dc_status_expired'),
    exhausted: t('dc_status_exhausted'),
    inactive: t('dc_status_inactive')
  };

  const getPrivilegeText = (tipo) => (tipo === 2 ? t('dc_user_type_2') : t('dc_user_type_3'));

  const searchLower = searchTerm.trim().toLowerCase();
  const searched = discountCodes.filter((code) => !searchLower || (
    (code.codigo_promocional || '').toLowerCase().includes(searchLower)
    || (code.usuario_nombre || '').toLowerCase().includes(searchLower)
    || (code.usuario_email || '').toLowerCase().includes(searchLower)
  ));

  const statusCounts = STATUS_FILTERS.reduce((acc, filter) => {
    acc[filter] = filter === 'all' ? searched.length : searched.filter((code) => codeStatus(code) === filter).length;
    return acc;
  }, {});

  const visibleCodes = statusFilter === 'all'
    ? searched
    : searched.filter((code) => codeStatus(code) === statusFilter);

  const totalPages = Math.max(1, Math.ceil(visibleCodes.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pagedCodes = visibleCodes.slice(pageStart, pageStart + PAGE_SIZE);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code.codigo_promocional);
      setCopiedId(code.id);
      setTimeout(() => setCopiedId((current) => (current === code.id ? null : current)), 1600);
    } catch (err) {
      console.error('No se pudo copiar el código:', err);
    }
  };

  const lockedFields = Boolean(editingCode && Number(editingCode.usos_actuales) > 0);
  const activeUnused = Boolean(editingCode && !lockedFields && codeStatus(editingCode) === 'active');

  if (loading) {
    return (
      <div className="home-container tp-dash tp-discounts tp-discounts--center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="home-container tp-dash tp-discounts">
      <div className="dashboard-container">
      <header className="tp-dash__head">
          <div className="tp-dash__heading">
            <SectionEyebrow>{t('dc_title')}</SectionEyebrow>
            <p className="tp-dash__lead">{t('dc_subtitle')}</p>
          </div>
          <button
            type="button"
            className="btn btn--primary tp-dash__new"
            onClick={openCreate}
          >
            <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
            {t('dc_create')}
          </button>
        </header>

      {error && <p className="tp-form__alert" role="alert">{error}</p>}

      <div className="tp-dash__toolbar">
        <div className="tp-dash__tabs" role="group" aria-label={t('dc_filter_label')}>
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              className={`tp-dash__tab ${statusFilter === filter ? 'is-active' : ''}`.trim()}
              aria-pressed={statusFilter === filter}
              onClick={() => { setStatusFilter(filter); setPage(1); }}
            >
              {filter === 'all' ? t('dc_filter_all') : statusLabel[filter]}
              <span className="tp-dash__tab-count">{statusCounts[filter]}</span>
            </button>
          ))}
        </div>

        <div className="tp-dash__controls">
          <label className="tp-dash__search">
            <Search size={16} strokeWidth={2} aria-hidden="true" />
            <span className="tp-sr-only">{t('dc_search_placeholder')}</span>
            <input
              type="search"
              placeholder={t('dc_search_placeholder')}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            />
          </label>
        </div>
      </div>

      {discountCodes.length === 0 && (
        <div className="tp-dash__empty">
          <span className="tp-dash__empty-icon" aria-hidden="true">
            <Tag size={26} strokeWidth={1.75} />
          </span>
          <p className="tp-dash__empty-title">{t('dc_no_codes')}</p>
          <p className="tp-dash__empty-text">{t('dc_no_codes_created')}</p>
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
            {t('dc_create_first')}
          </button>
        </div>
      )}

      {discountCodes.length > 0 && visibleCodes.length === 0 && (
        <p className="tp-dash__no-match">{t('dc_no_results')}</p>
      )}

      {visibleCodes.length > 0 && (
        <ul className="tp-dash__list">
          {pagedCodes.map((code) => {
            const status = codeStatus(code);
            const limit = code.limite_usos;
            const used = code.usos_actuales || 0;
            const progress = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;

            return (
              <li key={code.id} className="tp-dash__item">
                <div className="tp-dash__main">
                  <div className="tp-dash__title-row">
                    <h3 className="tp-dash__title tp-discounts__code">{code.codigo_promocional}</h3>
                    <span className={`tp-dash__status is-${status}`}>{statusLabel[status]}</span>
                  </div>
                  <div className="tp-dash__meta">
                    <span className="tp-discounts__privilege">
                      {getPrivilegeText(code.tipo_privilegio)}
                      {' · '}
                      {code.duracion_dias
                        ? t('dc_duration_days').replace('{days}', code.duracion_dias)
                        : t('dc_duration_none')}
                    </span>
                    <span className="tp-dash__dates">
                      <CalendarDays size={14} strokeWidth={2} aria-hidden="true" />
                      {formatDate(code.fecha_inicio)}
                      {code.fecha_fin ? ` – ${formatDate(code.fecha_fin)}` : ` · ${t('dc_no_end_date')}`}
                    </span>
                    {code.usuario_nombre && (
                      <span className="tp-dash__dates">
                        <UserRound size={14} strokeWidth={2} aria-hidden="true" />
                        {t('dc_used_by')} {code.usuario_nombre}
                      </span>
                    )}
                  </div>
                </div>

                <div className="tp-dash__progress">
                  {limit ? (
                    <span className="tp-dash__bar" aria-hidden="true">
                      <span className="tp-dash__bar-fill" style={{ width: `${progress}%` }} />
                    </span>
                  ) : null}
                  <span className="tp-dash__responses">
                    <Tag size={14} strokeWidth={2} aria-hidden="true" />
                    {limit
                      ? t('dc_uses_of').replace('{used}', used).replace('{total}', limit)
                      : t('dc_uses_unlimited').replace('{used}', used)}
                  </span>
                  {Number(code.usos_activos) > 0 && (
                    <span className="tp-dash__responses">
                      <UserRound size={14} strokeWidth={2} aria-hidden="true" />
                      {t('dc_active_now').replace('{count}', code.usos_activos)}
                    </span>
                  )}
                </div>

                <div className="tp-dash__actions">
                  <button
                    type="button"
                    className="btn btn--outline btn--sm tp-dash__primary"
                    onClick={() => copyCode(code)}
                  >
                    {copiedId === code.id
                      ? <Check size={15} strokeWidth={2.5} aria-hidden="true" />
                      : <Copy size={15} strokeWidth={2} aria-hidden="true" />}
                    {copiedId === code.id ? t('common_copied') : t('dc_copy_code')}
                  </button>

                  <ActionMenu
                    label={t('dash_more_actions')}
                    groups={[{
                      key: 'code',
                      items: [
                        {
                          key: 'copy',
                          label: t('dc_copy_code'),
                          icon: <Copy size={16} />,
                          onClick: () => copyCode(code)
                        },
                        {
                          key: 'edit',
                          label: t('dc_edit_code'),
                          icon: <Pencil size={16} />,
                          onClick: () => openEdit(code)
                        },
                        {
                          key: 'toggle',
                          label: Number(code.activo) === 0 ? t('dc_activate') : t('dc_deactivate'),
                          icon: Number(code.activo) === 0 ? <RotateCcw size={16} /> : <Ban size={16} />,
                          onClick: () => toggleActive(code)
                        },
                        {
                          key: 'delete',
                          label: t('dc_delete_code'),
                          icon: <Trash2 size={16} />,
                          danger: true,
                          disabled: used > 0,
                          onClick: () => handleDeleteCode(code.id)
                        }
                      ]
                    }]}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {visibleCodes.length > 0 && (
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onChange={setPage}
          total={visibleCodes.length}
          pageSize={PAGE_SIZE}
          summary={t('dash_page_summary')
            .replace('{from}', pageStart + 1)
            .replace('{to}', pageStart + pagedCodes.length)
            .replace('{total}', visibleCodes.length)}
          labels={{
            nav: t('dash_pagination_label'),
            prev: t('dash_page_prev'),
            next: t('dash_page_next'),
            page: (n) => t('dash_page_n').replace('{n}', n)
          }}
        />
      )}

      <Modal
        open={showCreateModal}
        onOpenChange={(open) => { if (!open) closeForm(); }}
        variant="edit"
        title={editingCode ? t('dc_edit_code') : t('dc_create_code')}
        description={editingCode ? editingCode.codigo_promocional : undefined}
        cancelText={t('common_cancel')}
        confirmText={editingCode ? t('dc_save') : t('dc_create')}
        confirmDisabled={saving}
        onCancel={closeForm}
        onConfirm={() => { handleSubmitForm(); return false; }}
        size="md"
      >
        <div className="tp-form tp-form--bare tp-setup">
          {lockedFields && (
            <p className="tp-discounts__locked" role="note">
              {t('dc_locked_used').replace('{count}', editingCode.usos_actuales)}
            </p>
          )}

          {activeUnused && (
            <p className="tp-discounts__heads-up" role="note">
              {t('dc_active_unused_warning')}
            </p>
          )}

          <FormField
            id="dc-code"
            label={t('dc_code_label')}
            type="text"
            value={formData.codigo_promocional}
            placeholder={t('dc_code_placeholder')}
            readOnly={lockedFields}
            icon={lockedFields ? <Lock size={15} strokeWidth={2} /> : undefined}
            onChange={(e) => setFormData({ ...formData, codigo_promocional: e.target.value.toUpperCase() })}
          />

          <div className="tp-setup__dates">
            <FormField
              id="dc-start"
              label={t('dc_start_date')}
              type="date"
              value={formData.fecha_inicio}
              readOnly={lockedFields}
              icon={lockedFields ? <Lock size={15} strokeWidth={2} /> : undefined}
                onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
            />
            {formData.hasEndDate && (
              <FormField
                id="dc-end"
                label={t('dc_end_date')}
                type="date"
                value={formData.fecha_fin}
                min={formData.fecha_inicio || undefined}
                hint={editingCode ? t('dc_end_date_hint') : undefined}
                onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
              />
            )}
          </div>

          <Checkbox
            checked={formData.hasEndDate}
            onChange={(e) => setFormData({ ...formData, hasEndDate: e.target.checked, fecha_fin: e.target.checked ? formData.fecha_fin : '' })}
          >
            {t('dc_has_end_date')}
          </Checkbox>

          <div className="tp-discounts__field">
            <label className="tp-discounts__label" htmlFor="dc-privilege">{t('dc_privilege')}</label>
            <SelectMenu
              id="dc-privilege"
              className="tp-discounts__plan"
              inline
              disabled={lockedFields}
              icon={lockedFields ? <Lock size={15} strokeWidth={2} /> : undefined}
              label={t('dc_privilege')}
              value={formData.tipo_privilegio}
              onChange={(value) => setFormData({ ...formData, tipo_privilegio: value })}
              options={[
                { value: 2, label: t('dc_user_type_2') },
                { value: 3, label: t('dc_user_type_3') }
              ]}
            />
          </div>

          <FormField
            id="dc-duration"
            label={t('dc_duration')}
            type="number"
            min="1"
            max={MAX_DURATION_DAYS}
            value={formData.duracion_dias}
            hint={lockedFields ? t('dc_duration_hint_used') : t('dc_duration_hint')}
            onChange={(e) => setFormData({ ...formData, duracion_dias: e.target.value })}
          />

          <Checkbox
            checked={formData.hasUsageLimit}
            onChange={(e) => setFormData({ ...formData, hasUsageLimit: e.target.checked, limite_usos: e.target.checked ? formData.limite_usos : '' })}
          >
            {t('dc_has_usage_limit')}
          </Checkbox>

          {formData.hasUsageLimit && (
            <FormField
              id="dc-limit"
              label={t('dc_limit')}
              type="number"
              min={Math.max(1, Number(editingCode ? editingCode.usos_actuales : 0) || 0)}
              value={formData.limite_usos}
              hint={lockedFields
                ? t('dc_limit_min_hint').replace('{count}', editingCode.usos_actuales)
                : undefined}
              onChange={(e) => setFormData({ ...formData, limite_usos: e.target.value })}
            />
          )}

          {formError && <p className="tp-form__alert" role="alert">{formError}</p>}
        </div>
      </Modal>

      <Modal
        open={Boolean(notice)}
        onOpenChange={(open) => { if (!open) { const after = notice && notice.after; setNotice(null); if (after) after(); } }}
        variant="warning"
        title={notice ? notice.title : ''}
        description={notice ? notice.text : ''}
        confirmText={t('common_understood')}
        onConfirm={() => { const after = notice && notice.after; setNotice(null); if (after) after(); }}
      />

      <Modal
        open={showDeleteConfirm}
        onOpenChange={(open) => { if (!open) cancelDelete(); }}
        variant="delete"
        title={t('dc_delete_code')}
        description={codeToDelete ? codeToDelete.codigo_promocional : ''}
        cancelText={t('common_cancel')}
        confirmText={t('dc_delete_code')}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />
      </div>
    </div>
  );
};

export default DiscountCodes;
